import React, { useState } from "react";
import { Modal } from "semantic-ui-react";
import Select from 'react-select';
import ProgressLoader from "../../Common/ProgressLoader";
import BasicButton from '../../Common/Button';
import { getParsedList } from '../../../Utils/helpers';
import { RISK_LEVELS } from '../../../Utils/constants';

interface BulkAddBanksModalProps {
    open: boolean;
    loading: boolean;
    selectedOffices: any[];
    lowRiskProcessors: any[];
    highRiskProcessors: any[];
    handleCancel: () => void;
    handleApply: (addLowRisk: string[], addHighRisk: string[]) => Promise<any[] | null>;
    config?: any;
}

type Step = 'select' | 'preview' | 'result';

// Offices are identified to users by their office code (e.g. "OF1451") and/or name —
// never the internal numeric database id, which means nothing to anyone outside the DB.
const officeLabel = (office: any) => {
    if (!office) return '';
    if (office.office_code && office.office_name) return `${office.office_code} — ${office.office_name}`;
    return office.office_code || office.office_name || '';
};

const BulkAddBanksModal = (props: BulkAddBanksModalProps) => {
    const { open, loading, selectedOffices, lowRiskProcessors, highRiskProcessors, handleCancel, handleApply, config } = props;
    const [step, setStep] = useState<Step>('select');
    const [selectedAddLowRisk, setSelectedAddLowRisk] = useState<any[]>([]);
    const [selectedAddHighRisk, setSelectedAddHighRisk] = useState<any[]>([]);
    const [results, setResults] = useState<any[]>([]);

    const addLowRiskValues = selectedAddLowRisk.map((item: any) => item.value);
    const addHighRiskValues = selectedAddHighRisk.map((item: any) => item.value);
    const hasSelection = addLowRiskValues.length > 0 || addHighRiskValues.length > 0;

    // A bank already on every selected office isn't a real "add" for any of them — hide it
    // from the picker so it can't be chosen in the first place, rather than silently
    // dropping it later in the preview with no explanation.
    const selectableLowRiskProcessors = lowRiskProcessors.filter((processor: any) =>
        selectedOffices.some((office: any) => !(getParsedList(office.allowed_low_risk_processors) || []).includes(processor.value))
    );
    const selectableHighRiskProcessors = highRiskProcessors.filter((processor: any) =>
        selectedOffices.some((office: any) => !(getParsedList(office.allowed_high_risk_processors) || []).includes(processor.value))
    );

    // Only ADDS banks — never removes or overwrites what an office already has.
    const officePreview = selectedOffices.map((office: any) => {
        const currentLow: string[] = getParsedList(office.allowed_low_risk_processors) || [];
        const currentHigh: string[] = getParsedList(office.allowed_high_risk_processors) || [];
        const officeRiskLevels: string[] = getParsedList(office.allowed_risk_levels) || [];
        const newLow = addLowRiskValues.filter((bank: string) => !currentLow.includes(bank));
        const newHigh = addHighRiskValues.filter((bank: string) => !currentHigh.includes(bank));

        // The office doesn't have this Risk Level enabled at all — a bank without its
        // Risk Level enabled would otherwise be a silent invalid state, so confirming
        // will enable the Risk Level too, not just add the bank.
        const riskLevelsToAdd: string[] = [];
        if (newLow.length > 0 && !officeRiskLevels.includes(RISK_LEVELS.LOW_RISK)) {
            riskLevelsToAdd.push(RISK_LEVELS.LOW_RISK);
        }
        if (newHigh.length > 0 && !officeRiskLevels.includes(RISK_LEVELS.HIGH_RISK)) {
            riskLevelsToAdd.push(RISK_LEVELS.HIGH_RISK);
        }

        return {
            office_id: office.office_id,
            office_label: officeLabel(office),
            newLow,
            newHigh,
            noChange: newLow.length === 0 && newHigh.length === 0,
            riskLevelsToAdd,
        };
    });

    const officesGainingRiskLevels = officePreview.filter((row) => row.riskLevelsToAdd.length > 0);
    // Every selected office already has every selected bank — applying would be a pure
    // no-op, which is indistinguishable from "Confirm & Apply silently did nothing."
    const allSelectionsAreDuplicates = hasSelection && officePreview.every((row) => row.noChange);

    const resetState = () => {
        setStep('select');
        setSelectedAddLowRisk([]);
        setSelectedAddHighRisk([]);
        setResults([]);
    };

    const handleClose = () => {
        resetState();
        handleCancel();
    };

    const handleConfirmApply = async () => {
        const response = await handleApply(addLowRiskValues, addHighRiskValues);
        // null means the request failed — the error is already shown via the page-level
        // overlay, so stay on this step rather than showing a misleading empty "complete".
        if (response === null) return;
        setResults(response);
        setStep('result');
    };

    return (
        <Modal
            centered
            size='small'
            open={open}
            onClose={handleClose}
            closeOnEscape={false}
            closeOnDimmerClick={false}
        >
            <Modal.Content>
                <div className="close-button">
                    <button className="email-activity-close-button" onClick={handleClose}>X</button>
                </div>
                <ProgressLoader loading={loading} size='small' />

                {step === 'select' && (
                    <div className="user-form">
                        <h3>Bulk Add Banks — {selectedOffices.length} {selectedOffices.length > 1 ? 'offices' : 'office'} selected</h3>
                        <p>Banks selected here are added to each office&apos;s existing Bank list. Nothing already assigned is removed or changed, and Risk Levels/RMs are untouched.</p>
                        <div className="fields">
                            <div className="four wide field"><p>Add Low Risk Banks :</p></div>
                            <div className="twelve wide field">
                                <Select
                                    isMulti={true}
                                    options={selectableLowRiskProcessors}
                                    value={selectedAddLowRisk}
                                    onChange={(value: any) => setSelectedAddLowRisk(value || [])}
                                    placeholder='Select Bank(s) to add'
                                />
                            </div>
                        </div>
                        <div className="fields">
                            <div className="four wide field"><p>Add High Risk Banks :</p></div>
                            <div className="twelve wide field">
                                <Select
                                    isMulti={true}
                                    options={selectableHighRiskProcessors}
                                    value={selectedAddHighRisk}
                                    onChange={(value: any) => setSelectedAddHighRisk(value || [])}
                                    placeholder='Select Bank(s) to add'
                                />
                            </div>
                        </div>
                    </div>
                )}

                {step === 'preview' && (
                    <div className="user-form">
                        <h3>Preview changes</h3>
                        <p>Review what will change before applying.</p>
                        {allSelectionsAreDuplicates && (
                            <div style={{ background: '#FBEAEA', color: '#B3392C', padding: '10px 14px', borderRadius: '6px', marginBottom: '14px' }}>
                                Selected banks already exist. Please recheck your selection.
                            </div>
                        )}
                        {officesGainingRiskLevels.length > 0 && (
                            <div style={{ background: '#E4EEEB', color: '#2F6E63', padding: '10px 14px', borderRadius: '6px', marginBottom: '14px' }}>
                                ℹ {officesGainingRiskLevels.length} {officesGainingRiskLevels.length > 1 ? 'offices' : 'office'} don't have the matching Risk Level enabled yet for the bank(s) being added — confirming will enable it on those offices too, so the bank isn't left assigned without its Risk Level. See flagged rows below.
                            </div>
                        )}
                        <table className="ui celled table">
                            <thead>
                                <tr>
                                    <th>Office</th>
                                    <th>Low Risk Banks to add</th>
                                    <th>High Risk Banks to add</th>
                                </tr>
                            </thead>
                            <tbody>
                                {officePreview.map((row: any) => {
                                    const lowRiskNoteHere = row.riskLevelsToAdd.includes(RISK_LEVELS.LOW_RISK);
                                    const highRiskNoteHere = row.riskLevelsToAdd.includes(RISK_LEVELS.HIGH_RISK);
                                    return (
                                        <tr key={row.office_id}>
                                            <td>{row.office_label}</td>
                                            <td>
                                                {addLowRiskValues.length === 0
                                                    ? <span style={{ color: '#8A9490' }}>—</span>
                                                    : row.newLow.length === 0
                                                        ? <span style={{ color: '#8A9490' }}>No change</span>
                                                        : row.newLow.join(', ')}
                                                {lowRiskNoteHere && (
                                                    <div style={{ color: '#2F6E63', fontSize: '12.5px', marginTop: '4px' }}>
                                                        Will also enable Low Risk as a Risk Level for this office
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                {addHighRiskValues.length === 0
                                                    ? <span style={{ color: '#8A9490' }}>—</span>
                                                    : row.newHigh.length === 0
                                                        ? <span style={{ color: '#8A9490' }}>No change</span>
                                                        : row.newHigh.join(', ')}
                                                {highRiskNoteHere && (
                                                    <div style={{ color: '#2F6E63', fontSize: '12.5px', marginTop: '4px' }}>
                                                        Will also enable High Risk as a Risk Level for this office
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {step === 'result' && (
                    <div className="user-form">
                        <h3>Bulk update complete</h3>
                        <table className="ui celled table">
                            <thead>
                                <tr>
                                    <th>Office</th>
                                    <th>Result</th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((row: any) => {
                                    const office = selectedOffices.find((o: any) => o.office_id === row.office_id);
                                    return (
                                        <tr key={row.office_id}>
                                            <td>{officeLabel(office)}</td>
                                            <td style={{ color: row.success ? '#2E7D46' : '#B3392C' }}>
                                                {row.success ? 'Updated' : `Failed: ${row.error}`}
                                                {row.success && row.risk_levels_added?.length > 0 && (
                                                    <div style={{ color: '#2F6E63', fontSize: '12.5px', marginTop: '4px' }}>
                                                        Also enabled {row.risk_levels_added.join(' and ')} as a Risk Level for this office
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Modal.Content>
            <Modal.Actions>
                {step === 'select' && (
                    <>
                        <BasicButton
                            textColor={config?.client_config_fe?.button_color}
                            backgroundColor={'#FFFFFF'}
                            size='18px'
                            buttonText="CANCEL"
                            className="save-button button-border"
                            onClick={handleClose}
                        />
                        <button
                            className={hasSelection ? "save-button button-border" : "not-active save-button button-border"}
                            style={{ background: config?.client_config_fe?.button_color }}
                            onClick={() => hasSelection && setStep('preview')}
                        >PREVIEW</button>
                    </>
                )}
                {step === 'preview' && (
                    <>
                        <BasicButton
                            textColor={config?.client_config_fe?.button_color}
                            backgroundColor={'#FFFFFF'}
                            size='18px'
                            buttonText="BACK"
                            className="save-button button-border"
                            onClick={() => setStep('select')}
                        />
                        <button
                            className={allSelectionsAreDuplicates ? "not-active save-button button-border" : "save-button button-border"}
                            style={{ background: config?.client_config_fe?.button_color }}
                            onClick={() => !allSelectionsAreDuplicates && handleConfirmApply()}
                        >CONFIRM &amp; APPLY</button>
                    </>
                )}
                {step === 'result' && (
                    <button
                        className="save-button button-border"
                        style={{ background: config?.client_config_fe?.button_color }}
                        onClick={handleClose}
                    >DONE</button>
                )}
            </Modal.Actions>
        </Modal>
    );
};

export default BulkAddBanksModal;
